import { SYS_BORDER_RADIUS_MAX, SYS_COLOR_CANVAS_200_DARK } from '@oneaudi/unified-web-alpha-common';
import { IconButton, TextButton } from '@oneaudi/unified-web-alpha-components';
import { useEffect } from 'react';
import styled from 'styled-components';
import { selectionStepAVPCamera } from '~/app/compositions/content/gallery/avp/types';
import { Controls } from '~/app/compositions/content/gallery/gallery';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useCtxConfigurator } from '~/app/pages/configurator/context/ctx-configurator';
import { useBreakPoint } from '~/hooks/responsive/useBreakPoint';
import { DATA_TEST_ID } from '~/test/data-test-id';

export const ControlsRight = ({
	activeIndex,
	imagesLength,
	onPrevious,
	onNext,
	controls,
	fullscreen,
	handleFullScreenChange,
	id,
}: {
	activeIndex: number;
	imagesLength: number;
	onPrevious: () => void;
	onNext: () => void;
	controls?: Controls;
	fullscreen: boolean;
	handleFullScreenChange: (fullscreen: boolean) => void;
	id: string;
}) => {
	const { isMobile, isMobileView } = useBreakPoint();
	const { i18n } = useI18n();

	const handleResize = () => {
		if ((isMobile || isMobileView) && fullscreen) {
			handleFullScreenChange(false);
		}
	};

	useEffect(() => {
		window?.addEventListener('resize', handleResize);
		return () => window?.removeEventListener('resize', handleResize);
	}, [isMobile, isMobileView, fullscreen]);

	return (
		<>
			<div className={'controls-right'}>
				{controls?.fullscreen && !fullscreen && !(isMobile || isMobileView) && (
					<IconButton
						className="control-button"
						variant="secondary"
						size="sm"
						icon={'resize-increase'}
						onClick={() => handleFullScreenChange(true)}
						aria-label={i18n('ngc.actions.openFullscreen')}
						data-testid={DATA_TEST_ID.GALLERY_FULL_SCREEN_BUTTON}
						id={id + '_' + DATA_TEST_ID.GALLERY_FULL_SCREEN_BUTTON}
					/>
				)}
				<IconButtonWrapper className="control-button" $disabled={activeIndex === 0}>
					<IconButton
						variant="secondary"
						size="sm"
						icon={'back'}
						onClick={onPrevious}
						disabled={activeIndex === 0}
						aria-label={i18n('ngc.actions.previous')}
						data-testid={DATA_TEST_ID.GALLERY_PREVIOUS_BUTTON}
						id={id + '_' + DATA_TEST_ID.GALLERY_PREVIOUS_BUTTON}
					/>
				</IconButtonWrapper>
				<IconButtonWrapper className="control-button" $disabled={activeIndex === imagesLength - 1}>
					<IconButton
						variant="secondary"
						size="sm"
						icon={'forward'}
						onClick={onNext}
						disabled={activeIndex === imagesLength - 1}
						aria-label={i18n('ngc.actions.next')}
						data-testid={DATA_TEST_ID.GALLERY_NEXT_BUTTON}
						id={id + '_' + DATA_TEST_ID.GALLERY_NEXT_BUTTON}
					/>
				</IconButtonWrapper>
			</div>
		</>
	);
};

// We need to overwrite the disabled state because right now the unified library component isn't accessible
const IconButtonWrapper = styled.div<{ $disabled: boolean }>`
	${(props) => props.$disabled && `background: ${SYS_COLOR_CANVAS_200_DARK};`}
	border-radius: ${SYS_BORDER_RADIUS_MAX};
`;

export const ControlsLeft = ({
	id,
	controls,
	toggleCompareMode,
	trackAvpClick,
}: {
	id: string;
	controls: Controls;
	toggleCompareMode: (manualEnableComparison?: boolean) => void;
	trackAvpClick: (open: boolean) => void;
}) => {
	const { i18n } = useI18n();
	const { isMobile, isMobileView } = useBreakPoint();
	const { setActive3DViews, setHideAVP, AVPSessionId, setAVPCameraId } = useCtxConfigurator();
	const { config } = useMarketConfig();

	const enableAvp = () => {
		if (!AVPSessionId) {
			setActive3DViews([id]);
		}
		setAVPCameraId(selectionStepAVPCamera[id as keyof typeof selectionStepAVPCamera]);
		setHideAVP(false);
		toggleCompareMode(false);
		trackAvpClick(true);
	};

	return (
		<div className={'controls-left'}>
			{config.hasJetStream &&
				config.enable3d &&
				controls.avp &&
				(isMobile || isMobileView ? (
					<IconButton
						className="control-button"
						variant="secondary"
						size="sm"
						icon={'3d'}
						onClick={enableAvp}
						aria-label={i18n('ngc.actions.openAVP')}
						data-testid={DATA_TEST_ID.GALLERY_3D_BUTTON}
					/>
				) : (
					<TextButton
						className="control-button"
						variant="secondary"
						size="sm"
						onClick={enableAvp}
						aria-label={i18n('ngc.actions.openAVP')}
						data-testid={DATA_TEST_ID.GALLERY_3D_BUTTON}>
						{i18n('ngc.avp.galleryControl')}
					</TextButton>
				))}
			{controls.compare && (
				<IconButton
					className="control-button"
					variant="secondary"
					size="sm"
					icon={'compare'}
					onClick={() => toggleCompareMode()}
					aria-label={i18n('ngc.actions.compare')}
					data-testid={DATA_TEST_ID.GALLERY_COMPARE_BUTTON}
				/>
			)}
		</div>
	);
};
